import React, { useEffect, useState } from "react";
import {  useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCard, creditPurchase, deleteCard, getCreditCards } from "apis/creditService";
import { CreditPurchaseRequest } from "@pages/Credits/types";
import CardSaveContainer from "./StripeSaveCard";
import CardDeleteModal from "./CardDeleteModal";

// Turns raw API/axios errors into a message a normal user can understand.
// Prefers a specific backend message (e.g. a Stripe decline reason) and never
// surfaces developer noise like "Request failed with status code 400".
const getFriendlyPaymentError = (err: any): string => {
  const backendMessage = err?.response?.data?.message;
  if (typeof backendMessage === "string" && backendMessage.trim()) {
    return backendMessage;
  }

  // Request was made but no response came back → network / timeout.
  if (err?.request && !err?.response) {
    return "We couldn't reach the server. Please check your connection and try again.";
  }

  const status = err?.response?.status;
  if (status === 400) {
    return "We couldn't process your payment. Please check your card details and try again.";
  }
  if (status === 401 || status === 403) {
    return "Your session has expired. Please log in again and retry the payment.";
  }
  if (status === 402) {
    return "Your card was declined. Please try a different card.";
  }
  if (status === 404) {
    return "This payment option is no longer available. Please refresh and try again.";
  }
  if (status === 409) {
    return "This payment may have already been processed. Please refresh before retrying.";
  }
  if (typeof status === "number" && status >= 500) {
    return "Something went wrong on our end. Please try again in a few moments.";
  }
  return "Something went wrong while processing your payment. Please try again.";
};

interface StripeCheckoutProps {
  creditPlanId: number;
  amount: number;
  currency: string;
  onClose: () => void;
  onBack: () => void;
  onPaymentSuccess?: () => void;
  isAutoPurchase: boolean;
  autoPurchaseCreditPlanId: number;
  autoPurchaseCreditLimit: number;
  isMonthlyPurchase: boolean;
  monthlyPurchaseCreditPlanId: number;
}

const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  creditPlanId,
  amount,
  currency,
  onClose,
  onBack,
  onPaymentSuccess,
  isAutoPurchase,
  autoPurchaseCreditPlanId,
  autoPurchaseCreditLimit,
  isMonthlyPurchase,
  monthlyPurchaseCreditPlanId,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showSaveCardModal, setShowSaveCardModal] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>("");
  const [creditCards, setCreditCards] = useState<CreditCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const closeAll = () => {
    setShowSaveCardModal(false);
    onClose();
  };

  const fetchCards = async () => {
    try {
      setCardsLoading(true);
      const cards = await getCreditCards();
      setCreditCards(cards);
      // Reset selection if no cards or previous selection is invalid
      if (cards.length > 0 && selectedCardIndex === null) {
        setSelectedCardIndex(0);
        setSelectedPaymentMethodId(cards[0].paymentMethodId);
      } else if (cards.length === 0) {
        setSelectedCardIndex(null);
        setSelectedPaymentMethodId("");
      }
    } catch (err) {
      console.error("Error fetching saved cards:", err);
      setError("Failed to load saved cards");
    } finally {
      setCardsLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const handlePayment = async () => {
    if (!stripe || !elements) return;

    // Guard the common case behind the 400 in the screenshot: no card chosen.
    if (!selectedPaymentMethodId) {
      setError("Please add or select a card to continue.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const paymentData: CreditPurchaseRequest = {
        creditPlanId,
        amount,
        paymentMethodId: selectedPaymentMethodId,
        currency,
        isAutoPurchase,
        autoPurchaseCreditPlanId,
        autoPurchaseCreditLimit,
        isMonthlyPurchase,
        monthlyPurchaseCreditPlanId,
      };

      // Call the creditPurchase API
      await creditPurchase(paymentData);

      setSuccess(true);

      // Close after a short delay to show success state
      setTimeout(() => {
        onClose();
        if (onPaymentSuccess) onPaymentSuccess();
      }, 1500);
    } catch (err: any) {
      // Backend returns Stripe decline details in err.response.data.message
      // (e.g. "Your card was declined."); otherwise fall back to a friendly,
      // status-based message instead of axios's raw "Request failed..." text.
      setError(getFriendlyPaymentError(err));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (paymentMethodId: string) => {
    setPaymentMethodId(paymentMethodId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!paymentMethodId) return;
    setLoading(true);
    try {
      await deleteCard({ paymentMethodId: paymentMethodId }); 
      await fetchCards(); // refresh list
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setPaymentMethodId(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setPaymentMethodId(null);
  };

  return (
    <div className="fixed inset-0 bg-gray-900/50 dark:bg-gray-900/80 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-lightgray-100">
          <h2 className="text-base font-semibold text-grease-100">Card Payment</h2>
          <button
            type="button"
            className="text-gray-600 bg-transparent rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
            onClick={closeAll}
          >
            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span className="sr-only">Close modal</span>
          </button>
        </div>

        <div className="p-4 flex flex-wrap gap-6 items-center justify-between bg-indigo-50 bg-opacity-60 border border-indigo-50 rounded-xl">
          <p className="text-lg text-grease-100 mb-1">Total Amount</p>
          <p className="text-2xl font-semibold text-grease-100">
            $ {amount.toFixed(2)} {currency.toUpperCase()}
          </p>
        </div>

        <div className="mb-6 mt-2">
          <p className=" font-semibold text-grease-100 mb-2">How would you like to pay?</p>

          {/* <div className="border mb-2 border-lightgray-100 rounded-lg p-4">
             <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                    },
                    },
                    hidePostalCode: true,
                    }}
                    /> 
                    </div> */}

          {/* Display saved cards */}

          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 mb-2">CREDIT & DEBIT CARDS</p>
          </div>
          {cardsLoading ? (
            <div className="text-sm text-gray-500 flex items-center">
              <svg className="animate-spin h-4 w-4 mr-2 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Loading saved cards...
            </div>
          ) : creditCards.length > 0 ? (
            <div className="space-y-2">
              {creditCards.map((card, index) => (
                <label
                  key={index}
                  className={`block border border-lightgray-100 rounded-lg p-3  cursor-pointer ${
                    selectedCardIndex === index ? "bg-indigo-50" : ""
                  }`}
                >
                  <div className="flex flex-row justify-between items-start gap-2 w-full h-[22px]">
                    <div className="flex flex-row items-center gap-5 w-[219.33px] h-[22px] ml-2">
                      <input
                        type="radio"
                        name="savedCard"
                        checked={selectedCardIndex === index}
                        onChange={() => {
                          setSelectedCardIndex(index);
                          setSelectedPaymentMethodId(card.paymentMethodId);
                        }}
                        className="w-5 h-5 rounded-full border-[#304CB2] text-[#304CB2] focus:ring-[#304CB2] cursor-pointer"
                      />
                      <div className="flex flex-row items-center gap-2 w-[179.33px] h-[22px]">
                        <span className="font-['Poppins'] font-semibold text-sm text-[#304CB2]">
                          {card.cardType.toUpperCase()} ending in {card.cardLast4}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="font-['Poppins'] font-normal text-[13px] text-[#262626]">
                        Expires {card.expirationMonth.toString().padStart(2, "0")}/{card.expirationYear}
                      </span>

                      {/* DELETE BUTTON */}
                      <button className="cursor-pointer" onClick={() => handleDelete(card.paymentMethodId)}>
                        <i className="st-delete text-[#FF4D4F] text-lg"></i>
                      </button>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No saved cards found</div>
          )}

          <div className="border border-lightgray-100 rounded-lg p-0 mt-2">
            <button type="button" onClick={() => setShowSaveCardModal(true)} className="flex flex-row items-center  gap-5 ml-5 ">
              <img className="w-5 my-4" src="/images/addButton.svg" alt="+" />

              <div className="flex flex-row items-center p-0 gap-2 w-[213px] h-[22px]">
                <span className="w-[213px] h-[22px] font-['Poppins'] font-normal text-[14px] leading-[22px] text-[#304CB2]">
                  Add a new debit or credit card
                </span>
              </div>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center transition-opacity duration-300 ease-in-out opacity-100">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              ></path>
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              ></path>
            </svg>
            Payment successful!
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handlePayment}
            disabled={!stripe || loading || success}
            className={`flex-1 py-3 px-4 rounded-lg text-white text-sm font-medium ${
              loading || !stripe || success ? "bg-[#FFBC00]/60" : "bg-[#FFBC00]"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : success ? (
              "Payment Complete"
            ) : (
              "Confirm Payment"
            )}
          </button>

          <button
            onClick={onBack}
            className="py-3 px-8 border border-lightgray-100 rounded-lg text-grease-100 text-sm font-medium hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>

      {showSaveCardModal && (
        <div className="fixed inset-0 bg-gray-900/50 dark:bg-gray-900/80 z-50 flex justify-center items-center">
          <CardSaveContainer
            onClose={() => setShowSaveCardModal(false)}
            onSaveSuccess={() => {
              fetchCards();
              setShowSaveCardModal(false);
            }}
          />
        </div>
      )}
      <CardDeleteModal isOpen={isDeleteModalOpen} onConfirm={confirmDelete} onCancel={cancelDelete} />
    </div>
  );
};

export default StripeCheckout;
